/**
 * @license
 * @copyright Copyright Motili Inc., 2025 All Rights Reserved
 */

import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
    create,
    getTemplate,
    update,
    mapProperty,
    clone,
} from 'admin/services/AdminTemplatesService';
import { getAccount } from 'common/services/AuthService';
import {
    mapTemplateState,
    postTemplateAction,
} from 'admin/stores/redux/AdminTemplate/utils';

const initialState = {
    loading: false,
    busy: false,
    template: null,
    error: null,
};

/**
 * Fetches a template by ID with related data
 */
export const fetchTemplate = createAsyncThunk(
    'adminTemplate/fetch',
    async ({ id }) => {
        const template = await getTemplate(id, {
            include: [
                {
                    relation: 'createdBy',
                    scope: {
                        fields: ['fullName'],
                    },
                },
                {
                    relation: 'updatedBy',
                    scope: {
                        fields: ['fullName'],
                    },
                },
            ],
        });
        const mappedTemplate = await mapProperty(template);
        return postTemplateAction(mappedTemplate);
    }
);

/**
 * Creates a new template
 */
export const createTemplate = createAsyncThunk(
    'adminTemplate/create',
    async ({ template }) => {
        return create(template);
    }
);

/**
 * Updates an existing template
 */
export const updateTemplate = createAsyncThunk(
    'adminTemplate/update',
    async ({ id, template }) => {
        const updated = await update(id, template);
        return postTemplateAction(updated);
    }
);

/**
 * Activates a template
 */
export const activateTemplate = createAsyncThunk(
    'adminTemplate/activate',
    async ({ id }) => {
        const template = await update(id, { active: true });
        return postTemplateAction(template);
    }
);

/**
 * Deactivates a template
 */
export const deactivateTemplate = createAsyncThunk(
    'adminTemplate/deactivate',
    async ({ id }) => {
        const template = await update(id, { active: false });
        return postTemplateAction(template);
    }
);

/**
 * Clones an existing template
 */
export const cloneTemplate = createAsyncThunk(
    'adminTemplate/clone',
    async ({ id, name }) => {
        const template = await clone(id, name);
        return postTemplateAction(template);
    }
);

const adminTemplateSlice = createSlice({
    name: 'adminTemplate',
    initialState,
    reducers: {
        /**
         * Adds a contact to the template
         */
        addContact: (state, action) => {
            const contact = action.payload;
            const account = getAccount();
            const existingContacts = state.template?.template?.contacts || [];
            const contacts = [...existingContacts, contact];

            return {
                ...state,
                template: {
                    ...state.template,
                    template: {
                        ...state.template.template,
                        contacts,
                    },
                    updatedBy: {
                        id: account?.id,
                        fullName:
                            account?.firstName &&
                            `${account.firstName} ${account.lastName}`,
                    },
                },
            };
        },
        /**
         * Deletes a contact from the template by ID
         */
        deleteContact: (state, action) => {
            const id = action.payload;
            const account = getAccount();
            const contacts = state.template.template.contacts.filter(
                c => c.id !== id
            );

            return {
                ...state,
                template: {
                    ...state.template,
                    template: {
                        ...state.template.template,
                        contacts: [...contacts],
                    },
                    updatedBy: {
                        id: account?.id,
                        fullName:
                            account?.firstName &&
                            `${account.firstName} ${account.lastName}`,
                    },
                },
            };
        },
        /**
         * Updates template metadata
         */
        updateTemplateMeta: (state, action) => {
            const meta = action.payload;
            return {
                ...state,
                template: {
                    ...state.template,
                    template: {
                        ...state.template.template,
                        ...meta,
                    },
                },
            };
        },
        /**
         * Clears the error state
         */
        clearError: state => {
            return {
                ...state,
                error: null,
            };
        },
    },
    extraReducers: builder => {
        builder
            .addCase(fetchTemplate.pending, state => {
                return {
                    ...state,
                    loading: true,
                    template: null,
                    error: null,
                };
            })
            .addCase(fetchTemplate.fulfilled, (state, action) => {
                return {
                    ...state,
                    loading: false,
                    template: action.payload,
                };
            })
            .addCase(fetchTemplate.rejected, (state, action) => {
                return {
                    ...state,
                    loading: false,
                    error: action.error,
                };
            })
            .addCase(createTemplate.pending, state => {
                return {
                    ...state,
                    busy: true,
                    error: null,
                };
            })
            .addCase(createTemplate.fulfilled, (state, action) => {
                return {
                    ...state,
                    busy: false,
                    template: action.payload,
                };
            })
            .addCase(createTemplate.rejected, (state, action) => {
                return {
                    ...state,
                    busy: false,
                    error: action.error,
                };
            })
            .addCase(updateTemplate.pending, state => {
                return {
                    ...state,
                    busy: true,
                    error: null,
                };
            })
            .addCase(updateTemplate.fulfilled, (state, action) => {
                return mapTemplateState(state, action.payload);
            })
            .addCase(updateTemplate.rejected, (state, action) => {
                return {
                    ...state,
                    error: action.error,
                };
            })
            .addCase(activateTemplate.pending, state => {
                return {
                    ...state,
                    busy: true,
                    error: null,
                };
            })
            .addCase(activateTemplate.fulfilled, (state, action) => {
                return mapTemplateState(state, action.payload);
            })
            .addCase(activateTemplate.rejected, (state, action) => {
                return {
                    ...state,
                    busy: false,
                    error: action.error,
                };
            })
            .addCase(deactivateTemplate.pending, state => {
                return {
                    ...state,
                    busy: true,
                    error: null,
                };
            })
            .addCase(deactivateTemplate.fulfilled, (state, action) => {
                return mapTemplateState(state, action.payload);
            })
            .addCase(deactivateTemplate.rejected, (state, action) => {
                return {
                    ...state,
                    busy: false,
                    error: action.error,
                };
            })
            .addCase(cloneTemplate.pending, state => {
                return {
                    ...state,
                    busy: true,
                    error: null,
                };
            })
            .addCase(cloneTemplate.fulfilled, (state, action) => {
                return {
                    ...state,
                    busy: false,
                    template: action.payload,
                };
            })
            .addCase(cloneTemplate.rejected, (state, action) => {
                return {
                    ...state,
                    busy: false,
                    error: action.error,
                };
            });
    },
});

export const { addContact, deleteContact, updateTemplateMeta, clearError } =
    adminTemplateSlice.actions;

export default adminTemplateSlice.reducer;
